/* API activity log — a lightweight network panel accessible from the Admin page.
 * Useful on mobile where browser devtools aren't available. */

export const MAX_EVENTS = 100

export interface ApiLogEvent {
    seq: number
    tabId: string
    t: number
    kind: string
    data: Record<string, unknown>
}

const tabId = Math.random().toString(36).slice(2, 8)
export const tabLoadedAt = Date.now()
let seq = 0
const log: ApiLogEvent[] = []

const pushEvent = (kind: string, data: Record<string, unknown>): void => {
    if (log.length >= MAX_EVENTS) return
    log.push({ seq: seq++, tabId, t: Date.now(), kind, data })
}

export const getApiLog = (): ApiLogEvent[] => log.slice()
export const clearApiLog = (): void => {
    log.length = 0
}

/** Logs an outgoing API request and adds a trace ID header. Returns the augmented headers
 *  and a request ID to pass to logApiResponse/logApiError. Returns unchanged headers and
 *  an empty ID once the log is full. */
export const logApiRequest = (
    path: string, method: string, headers: Record<string, string>
): { requestId: string, headers: Record<string, string> } => {
    if (log.length >= MAX_EVENTS) return { requestId: '', headers }
    const requestId = Math.random().toString(36).slice(2, 8)
    pushEvent('api_send', { requestId, path, method })
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return { requestId, headers: { ...headers, 'X-Client-Request-Id': requestId } }
}

/** Logs a successful API response. Pass the requestId returned by logApiRequest. */
export const logApiResponse = (
    requestId: string, path: string, method: string, status: number, modifiedHeader: string | null
): void => {
    if (!requestId) return
    pushEvent('api_recv', { requestId, path, method, status, modifiedHeader: modifiedHeader ?? undefined })
}

/** Logs a network failure or error response. Pass the requestId returned by logApiRequest. */
export const logApiError = (requestId: string, path: string, method: string, error: string): void => {
    if (!requestId) return
    pushEvent('api_recv', { requestId, path, method, status: -1, error })
}

/** Logs detection of a stale API response that has been dropped to protect newer in-memory state. */
export const reportStaleResponse = (
    location: string, itemId: string, existingMod: string, itemMod: string, itemStatus: unknown
): void => {
    pushEvent('stale_api_response', { location, itemId, existingMod, itemMod, itemStatus })
}

const summariseEntriesState = (raw: string | null): Record<string, unknown> => {
    if (!raw) return { empty: true }
    try {
        const parsed = JSON.parse(raw) as Record<string, unknown>
        const entries = (parsed.entries ?? {}) as Record<string, { modified?: string }>
        const writes = (parsed.write ?? {}) as Record<string, unknown>
        const entryIds = Object.keys(entries)
        let mostRecentModified: string | null = null
        for (const id of entryIds) {
            const m = entries[id]?.modified
            if (typeof m === 'string' && (mostRecentModified === null || m > mostRecentModified)) {
                mostRecentModified = m
            }
        }
        return {
            entriesCount: entryIds.length,
            writeCount: Object.keys(writes).length,
            mostRecentModified,
            currentlyWriting: parsed.currentlyWriting ?? null
        }
    } catch {
        return { parseError: true }
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (!event.key || !['entries', 'appState'].includes(event.key)) return
        const data: Record<string, unknown> = { key: event.key, url: event.url }
        if (event.key === 'entries') Object.assign(data, summariseEntriesState(event.newValue))
        pushEvent('cross_tab_write', data)
    })
    ;(window as unknown as Record<string, unknown>).__diaryApiLog = getApiLog
}

export const relativeLoadTime = (): string => {
    const mins = Math.floor((Date.now() - tabLoadedAt) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
    const hrs = Math.floor(mins / 60)
    const rem = mins % 60
    return `${hrs}h ${rem}m ago`
}

const s = (v: unknown): string => v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v)

export const formatEvent = (e: ApiLogEvent): string => {
    const time = `T+${((e.t - tabLoadedAt) / 1000).toFixed(1)}s`
    const d = e.data
    switch (e.kind) {
        case 'api_send':
            return `${time}  →  ${s(d.method)} ${s(d.path)}`
        case 'api_recv':
            if (d.error) return `${time}  ✗  ${s(d.method)} ${s(d.path)}  ERROR: ${s(d.error)}`
            return `${time}  ←  ${s(d.method)} ${s(d.path)}  ${s(d.status)}`
              + (d.modifiedHeader ? `  (modified ${s(d.modifiedHeader)})` : '')
        case 'stale_api_response':
            return `${time}  ⚠  STALE ${s(d.location)} — ${s(d.itemId)}: local ${s(d.existingMod)}, server sent ${s(d.itemMod)}`
        case 'cross_tab_write':
            return `${time}  ✎  other tab wrote ${s(d.key)}`
              + (d.entriesCount != null ? ` (${s(d.entriesCount)} entries, newest ${s(d.mostRecentModified)})` : '')
        default:
            return `${time}  ${e.kind}  ${JSON.stringify(d)}`
    }
}
